import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, CalendarPlus, X, Trash2, Plus } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import { exportToCSV, exportToPDF } from '../utils/exportUtils';
import { DateInput, formatDisplayDate } from './DateFilter';
import { usePagedQuery, buildQuery } from './usePagedQuery';
import { Pagination, SearchBox } from './TableControls';

const API = process.env.REACT_APP_BACKEND_API_URL;

// Status with color coding, exactly as specified.
const STATUS_META = {
    P: { label: 'Present', bg: '#dcfce7', color: '#166534' },
    A: { label: 'Absent', bg: '#fee2e2', color: '#b91c1c' },
    HD: { label: 'Half Day', bg: '#dbeafe', color: '#1e40af' },
    L: { label: 'Leave', bg: '#fef9c3', color: '#854d0e' },
    WFH: { label: 'Work From Home', bg: '#f3e8ff', color: '#6b21a8' }
};
const STATUS_ORDER = ['P', 'A', 'HD', 'L', 'WFH'];
const SUN_META = { bg: '#fee2e2', color: '#b91c1c' };
const HOLIDAY_META = { bg: '#f1f5f9', color: '#64748b' };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;
const daysInMonth = (year, month) => new Date(year, month, 0).getDate(); // month is 1-12
const isSunday = (year, month, day) => new Date(year, month - 1, day).getDay() === 0;

const btnStyle = {
    display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0',
    padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer'
};

// AttendanceManagement is shared by Admin and the one designated Team Lead.
// Holiday management stays Admin-only even here (checked both by hiding the
// control and by the backend independently rejecting a non-Admin's request).
const AttendanceManagement = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin';
    const today = new Date();

    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [exporting, setExporting] = useState(false);

    const [activeCell, setActiveCell] = useState(null); // { recruiterId, date, recruiterName }
    const [cellRemarks, setCellRemarks] = useState('');
    const [savingCell, setSavingCell] = useState(false);

    const [showHolidays, setShowHolidays] = useState(false);

    // Server-side paging over the recruiter rows (sorted by name). Entries / holidays come back with each page,
    // entries only for the recruiters on that page.
    const sheetParams = useMemo(() => ({
        month, year, search: search.trim(), status: statusFilter,
        requester_name: user?.name || '', requester_role: user?.role || ''
    }), [month, year, search, statusFilter, user?.name, user?.role]);
    const table = usePagedQuery(`${API}/api/attendance`, sheetParams);
    const recruiters = table.rows;
    const loading = table.loading;
    const holidays = (table.extras && table.extras.holidays) || [];
    const [entries, setEntries] = useState([]); // [{ recruiter_id, date, status, remarks, updated_by, updated_by_role, updated_at }]
    useEffect(() => { setEntries((table.extras && table.extras.entries) || []); }, [table.extras]);
    const fetchSheet = table.refetch;

    const days = useMemo(() => Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1), [year, month]);
    const holidaySet = useMemo(() => new Set(holidays.map(h => h.date.split('T')[0])), [holidays]);
    const holidayByDate = useMemo(() => Object.fromEntries(holidays.map(h => [h.date.split('T')[0], h.name])), [holidays]);

    // entries[recruiter_id][date] -> entry, for O(1) cell lookups while rendering.
    const entryMap = useMemo(() => {
        const map = {};
        entries.forEach(e => {
            const d = e.date.split('T')[0];
            if (!map[e.recruiter_id]) map[e.recruiter_id] = {};
            map[e.recruiter_id][d] = e;
        });
        return map;
    }, [entries]);

    const openCell = (recruiterId, recruiterName, day) => {
        if (isSunday(year, month, day)) return; // never markable
        const date = toISODate(year, month, day);
        if (holidaySet.has(date)) return; // never markable
        const existing = entryMap[recruiterId]?.[date];
        setActiveCell({ recruiterId, date, recruiterName });
        setCellRemarks(existing?.remarks || '');
    };

    const saveCell = async (status) => {
        if (!activeCell) return;
        setSavingCell(true);
        try {
            const response = await fetch(`${API}/api/attendance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    recruiter_id: activeCell.recruiterId, date: activeCell.date, status,
                    remarks: cellRemarks, requester_name: user?.name, requester_role: user?.role
                })
            });
            const data = await response.json();
            if (!response.ok) {
                setError(data.error || 'Failed to save attendance.');
                return;
            }
            setEntries(prev => {
                const others = prev.filter(e => !(e.recruiter_id === activeCell.recruiterId && e.date.split('T')[0] === activeCell.date));
                return [...others, data];
            });
            setActiveCell(null);
        } catch (err) {
            console.error('Failed to save attendance:', err);
            setError('Server connection failed.');
        } finally {
            setSavingCell(false);
        }
    };

    // Recruiter-wise summary: counts per status, working days (month days minus
    // Sundays minus holidays), and Attendance % = (P + HD*0.5 + WFH) / working days.
    const summaryFor = (recruiterId, map = entryMap) => {
        const counts = { P: 0, A: 0, HD: 0, L: 0, WFH: 0 };
        days.forEach(day => {
            if (isSunday(year, month, day)) return;
            const date = toISODate(year, month, day);
            if (holidaySet.has(date)) return;
            const status = map[recruiterId]?.[date]?.status;
            if (status && counts[status] !== undefined) counts[status]++;
        });
        const workingDays = days.filter(day => !isSunday(year, month, day) && !holidaySet.has(toISODate(year, month, day))).length;
        const effectivePresent = counts.P + counts.HD * 0.5 + counts.WFH;
        const attendancePct = workingDays > 0 ? Math.round((effectivePresent / workingDays) * 1000) / 10 : 0;
        return { ...counts, workingDays, attendancePct };
    };

    const buildExportRows = (list, map) => list.map(r => {
        const sm = summaryFor(r.id, map);
        const row = { recruiter: r.name };
        days.forEach(day => {
            const date = toISODate(year, month, day);
            row[`d${day}`] = isSunday(year, month, day) ? 'SUN' : (holidaySet.has(date) ? 'H' : (map[r.id]?.[date]?.status || ''));
        });
        row.present = sm.P; row.absent = sm.A; row.halfDay = sm.HD; row.leave = sm.L; row.wfh = sm.WFH;
        row.workingDays = sm.workingDays; row.attendancePct = `${sm.attendancePct}%`;
        return row;
    });

    // Exports cover EVERY recruiter matching the current month / search / status filters, not just the visible page.
    const getExportRows = async () => {
        const res = await fetch(`${API}/api/attendance?${buildQuery({ ...sheetParams, all: 1 })}`);
        const body = await res.json().catch(() => null);
        if (!res.ok) alert((body && body.error) || 'Export failed');
        const map = {};
        (body.entries || []).forEach(e => {
            const d = e.date.split('T')[0];
            if (!map[e.recruiter_id]) map[e.recruiter_id] = {};
            map[e.recruiter_id][d] = e;
        });
        return buildExportRows(body.rows || body.recruiters || [], map);
    };
    const runExport = async (fn) => {
        setExporting(true);
        setError('');
        try { fn(await getExportRows()); } catch (err) { console.error('Attendance export failed:', err); setError('Export failed. Please try again.'); } finally { setExporting(false); }
    };

    const exportColumns = useMemo(() => [
        { key: 'recruiter', label: 'Recruiter Name' },
        ...days.map(day => ({ key: `d${day}`, label: String(day) })),
        { key: 'present', label: 'Present' }, { key: 'absent', label: 'Absent' }, { key: 'halfDay', label: 'Half Day' },
        { key: 'leave', label: 'Leave' }, { key: 'wfh', label: 'WFH' }, { key: 'workingDays', label: 'Working Days' },
        { key: 'attendancePct', label: 'Attendance %' }
    ], [days]);

    const exportFilename = `Attendance_${MONTH_NAMES[month - 1]}_${year}`;

    const handleExportExcel = (exportRows) => {
        // No XLSX library is assumed to be part of this app's dependencies — an
        // HTML-table-as-.xls Blob is a well-established, zero-dependency way to
        // produce a file Excel opens natively, same data as the CSV/PDF exports.
        const headerRow = exportColumns.map(c => `<th>${c.label}</th>`).join('');
        const bodyRows = exportRows.map((row, i) =>
            `<tr><td>${i + 1}</td>${exportColumns.map(c => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`
        ).join('');
        const html = `<table><thead><tr><th>S.No</th>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>`;
        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${exportFilename}.xls`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Attendance" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button onClick={() => navigate(isAdmin ? '/admin' : '/team-lead')} style={btnStyle}>
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Attendance</h2>
            </div>

            {error && (
                <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>
            )}
            {table.error && !error && (
                <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{table.error}</div>
            )}

            {/* Controls: Month/Year, Manage Holidays (Admin-only), Export */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', boxSizing: 'border-box' }}>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>Month</label>
                    <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>Year</label>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        {Array.from({ length: 6 }, (_, i) => today.getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>

                <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>Search Recruiter</label>
                    <SearchBox value={search} onChange={setSearch} placeholder="Recruiter name..." minWidth="190px" />
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>Status marked this month</label>
                    <select aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        <option value="">All</option>
                        {STATUS_ORDER.map(code => <option key={code} value={code}>{code} — {STATUS_META[code].label}</option>)}
                    </select>
                </div>

                {isAdmin && (
                    <button onClick={() => setShowHolidays(true)} style={btnStyle}>
                        <CalendarPlus size={14} /> Manage Holidays
                    </button>
                )}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button disabled={exporting} onClick={() => runExport(handleExportExcel)} style={btnStyle}><Download size={14} /> {exporting ? 'Preparing...' : 'Excel'}</button>
                    <button disabled={exporting} onClick={() => runExport((rows) => exportToCSV(rows, exportColumns, exportFilename))} style={btnStyle}><Download size={14} /> CSV</button>
                    <button disabled={exporting} onClick={() => runExport((rows) => exportToPDF(rows, exportColumns, exportFilename, `Attendance — ${MONTH_NAMES[month - 1]} ${year}`))} style={btnStyle}><Download size={14} /> PDF</button>
                </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {STATUS_ORDER.map(code => (
                    <span key={code} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#475569' }}>
                        <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: STATUS_META[code].bg, border: `1px solid ${STATUS_META[code].color}33` }} />
                        {code} = {STATUS_META[code].label}
                    </span>
                ))}
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#475569' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: SUN_META.bg }} /> SUN = Sunday (off)
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#475569' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: HOLIDAY_META.bg, border: '1px solid #cbd5e1' }} /> H = Holiday
                </span>
            </div>

            {/* The sheet itself: horizontal scroll for the day columns, same
                established pattern as other wide tables in this app, which also
                carries this through to mobile/tablet without separate layouts. */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflowX: 'auto', boxSizing: 'border-box' }}>
                {loading ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : (
                    <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '100%' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '8px', color: '#64748b', fontWeight: '700', textAlign: 'left', position: 'sticky', left: 0, background: '#fff', minWidth: '140px' }}>Recruiter</th>
                                {days.map(day => {
                                    const sunday = isSunday(year, month, day);
                                    const holiday = holidaySet.has(toISODate(year, month, day));
                                    return (
                                        <th key={day} style={{ padding: '4px', color: sunday ? SUN_META.color : '#64748b', background: sunday ? SUN_META.bg : (holiday ? HOLIDAY_META.bg : 'transparent'), fontWeight: '700', minWidth: '34px', textAlign: 'center' }}>
                                            {day}{sunday && <div style={{ fontSize: '9px' }}>SUN</div>}
                                        </th>
                                    );
                                })}
                                <th style={{ padding: '6px', color: '#64748b', fontWeight: '700', minWidth: '50px' }}>Working Days</th>
                                <th style={{ padding: '6px', color: '#166534', fontWeight: '700', minWidth: '36px' }}>P</th>
                                <th style={{ padding: '6px', color: '#1e40af', fontWeight: '700', minWidth: '36px' }}>HD</th>
                                <th style={{ padding: '6px', color: '#b91c1c', fontWeight: '700', minWidth: '36px' }}>A</th>
                                <th style={{ padding: '6px', color: '#64748b', fontWeight: '700', minWidth: '36px' }}>L</th>
                                <th style={{ padding: '6px', color: '#64748b', fontWeight: '700', minWidth: '36px' }}>WFH</th>
                                <th style={{ padding: '6px', color: '#64748b', fontWeight: '700', minWidth: '50px' }}>Attendance %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recruiters.map(r => {
                                const s = summaryFor(r.id);
                                return (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '6px 8px', fontWeight: '600', position: 'sticky', left: 0, background: '#fff' }}>{r.name}</td>
                                        {days.map(day => {
                                            const sunday = isSunday(year, month, day);
                                            const date = toISODate(year, month, day);
                                            const holidayName = holidayByDate[date];
                                            const entry = entryMap[r.id]?.[date];
                                            let cellBg = 'transparent', cellColor = '#94a3b8', cellText = '';
                                            if (sunday) { cellBg = SUN_META.bg; cellColor = SUN_META.color; cellText = ''; }
                                            else if (holidayName) { cellBg = HOLIDAY_META.bg; cellColor = HOLIDAY_META.color; cellText = 'H'; }
                                            else if (entry) { cellBg = STATUS_META[entry.status].bg; cellColor = STATUS_META[entry.status].color; cellText = entry.status; }
                                            return (
                                                <td
                                                    key={day}
                                                    title={holidayName || ''}
                                                    onClick={() => openCell(r.id, r.name, day)}
                                                    style={{
                                                        textAlign: 'center', padding: '4px 2px', cursor: (sunday || holidayName) ? 'not-allowed' : 'pointer',
                                                        background: cellBg, color: cellColor, fontWeight: '700'
                                                    }}
                                                >
                                                    {cellText}
                                                </td>
                                            );
                                        })}
                                        <td style={{ textAlign: 'center' }}>{s.workingDays}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#166534', }}>{s.P}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#1e40af', }}>{s.HD}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#b91c1c', }}>{s.A}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700' }}>{s.L}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700' }}>{s.WFH}</td>
                                        <td style={{ textAlign: 'center', fontWeight: '700' }}>{s.attendancePct}%</td>
                                    </tr>
                                );
                            })}
                            {recruiters.length === 0 && (
                                <tr><td colSpan={days.length + 8} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>{search.trim() || statusFilter ? 'No recruiters match these filters.' : 'No recruiters found.'}</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
            <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="recruiters" />

            {/* Status picker popup for one cell */}
            {activeCell && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '100%', maxWidth: '340px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                                {activeCell.recruiterName} — {formatDisplayDate(activeCell.date)}
                            </h3>
                            <button onClick={() => setActiveCell(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={16} /></button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                            {STATUS_ORDER.map(code => (
                                <button
                                    key={code}
                                    disabled={savingCell}
                                    onClick={() => saveCell(code)}
                                    style={{ background: STATUS_META[code].bg, color: STATUS_META[code].color, border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '700', fontSize: '13px', cursor: savingCell ? 'not-allowed' : 'pointer' }}
                                >
                                    {code} — {STATUS_META[code].label}
                                </button>
                            ))}
                        </div>
                        <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>Remarks (optional)</label>
                        <input
                            value={cellRemarks}
                            onChange={(e) => setCellRemarks(e.target.value)}
                            placeholder="Optional note..."
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box', fontSize: '13px' }}
                        />
                    </div>
                </div>
            )}

            {showHolidays && isAdmin && (
                <HolidayManager
                    user={user}
                    year={year}
                    onClose={() => setShowHolidays(false)}
                    onChanged={fetchSheet}
                />
            )}
        </div>
    );
};

// Admin-only holiday list for the selected year: add / delete.
const HolidayManager = ({ user, year, onClose, onChanged }) => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ date: '', name: '' });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const requesterParams = `requester_name=${encodeURIComponent(user?.name || '')}&requester_role=${encodeURIComponent(user?.role || '')}`;

    const fetchHolidays = useCallback(() => {
        setLoading(true);
        fetch(`${API}/api/holidays?year=${year}&${requesterParams}`)
            .then(res => res.json())
            .then(data => setHolidays(Array.isArray(data) ? data : []))
            .catch(err => console.error('Failed to fetch holidays:', err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);

    useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

    const handleAdd = async () => {
        setError('');

        if (!form.date || !form.name.trim()) {
            setError('Date and holiday name are required.');
            return;
        }

        setSaving(true);

        try {
            const response = await fetch(`${API}/api/holidays`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    date: form.date,
                    name: form.name.trim(),
                    requester_role: user?.role,
                    requester_name: user?.name || ''
                })
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Failed to add holiday.');
                return;
            }
            setForm({
                date: '',
                name: ''
            });
            fetchHolidays();
            onChanged?.();

        } catch (err) {
            console.error('Failed to add holiday:', err);
            setError('Server connection failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API}/api/holidays/${id}?requester_role=${encodeURIComponent(user?.role || '')}`, { method: 'DELETE' });
            if (!response.ok) {
                const data = await response.json();
                setError(data.error || 'Failed to delete holiday.');
                return;
            }
            fetchHolidays();
            onChanged?.();

        } catch (err) {
            console.error('Failed to delete holiday:', err);
            setError('Server connection failed.');
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1300, padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '22px', width: '100%', maxWidth: '440px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Manage Holidays — {year}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <DateInput value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                    <input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Holiday name" style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: 0 }} />
                    <button onClick={handleAdd} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#2563eb', color: '#fff', border: 'none', padding: '0 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}>
                        <Plus size={14} /> Add
                    </button>
                </div>
                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}

                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                    ) : holidays.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No holidays set for {year}.</div>
                    ) : (
                        holidays.map(h => (
                            <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ fontSize: '13px' }}>
                                    <strong>{formatDisplayDate(h.date)}</strong> — {h.name}
                                </span>
                                <button onClick={() => handleDelete(h.id)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '5px 8px', borderRadius: '5px', cursor: 'pointer' }}>
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendanceManagement;